/**
 * "Were you invited? Check again" in the blocking dialog for a NONE person (TMMAPP-0158 item 2): the
 * shared reconcile, then `/me` is re-read. A started guest seat opens the gate (the dialog goes with
 * it); otherwise the live line says no invitation was found for the signed-in email, or that the check
 * couldn't run. At most one check a minute, like `SharedLanding`'s Retry; the last check's time is the
 * session's (`subscribeGate`), so the cooldown survives the dialog's remount (TMMAPP-0160 item 8).
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reconcileShares } from '@/features/sharing/reconcileShares';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { invitationCheckWait, markInvitationCheck } from '@/session/subscribeGate';

const COOLDOWN_MS = 60_000;

type Result = 'checking' | 'absent' | 'unavailable' | null;

export function InvitationCheck() {
  const { t } = useTranslation();
  const me = useMe();
  const refetch = useMeRefetch();
  const [result, setResult] = useState<Result>(null);
  const [cooldown, setCooldown] = useState(() => invitationCheckWait(COOLDOWN_MS) > 0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(false), invitationCheckWait(COOLDOWN_MS));
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const check = () => {
    if (cooldown || result === 'checking') return;
    markInvitationCheck();
    setCooldown(true);
    setResult('checking');
    void reconcileShares(refetch, () => mounted.current).then((outcome) => {
      if (!mounted.current) return;
      if (outcome.kind === 'answered' && typeof outcome.me !== 'string') {
        // A seat started: the gate opens and this dialog closes with it.
        setResult(
          outcome.me.tier !== 'NONE'
            ? null
            : outcome.upstreamUnavailable
              ? 'unavailable'
              : 'absent',
        );
      } else {
        setResult('unavailable');
      }
    });
  };

  const message =
    result === 'absent'
      ? me.user.email
        ? t('subscribeDialog.invitationAbsent', { email: me.user.email })
        : t('subscribeDialog.invitationAbsentNoEmail')
      : result === 'unavailable'
        ? t('subscribeDialog.invitationUnavailable')
        : '';

  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }} data-invitation-check="">
      <Button
        variant="text"
        size="small"
        loading={result === 'checking'}
        aria-disabled={cooldown}
        onClick={check}
      >
        {t('subscribeDialog.checkInvitation')}
      </Button>
      <Typography
        variant="body2"
        role="status"
        aria-live="polite"
        sx={{ overflowWrap: 'anywhere' }}
      >
        {message}
      </Typography>
    </Stack>
  );
}
